import React from "react";
import { FileText, Target, Eye, Zap, Users, Award } from "lucide-react";

export default function About() {
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
      <div className="grid lg:grid-cols-2 gap-14 items-center mb-20">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold mb-3">About</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">About Simple Invoice</h1>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            Simple Invoice is a free online tool built to help freelancers, consultants and small businesses create beautiful, professional invoices in seconds — without an account, without ads, and without compromise.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            No signup. No data uploaded. Everything happens right in your browser, so your billing details stay yours.
          </p>
        </div>
        <div className="rounded-3xl overflow-hidden border border-border shadow-xl">
          <img data-testid="about-hero-img" src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?w=1200" alt="Team collaboration" className="w-full h-full object-cover" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-20">
        <div className="rounded-2xl bg-card border border-border p-8">
          <Target className="text-blue-600 mb-4" />
          <h3 className="font-heading font-bold text-xl">Our Mission</h3>
          <p className="mt-3 text-muted-foreground leading-relaxed">Make professional invoicing effortless for everyone — from indie freelancers to family-run businesses.</p>
        </div>
        <div className="rounded-2xl bg-card border border-border p-8">
          <Eye className="text-blue-600 mb-4" />
          <h3 className="font-heading font-bold text-xl">Our Vision</h3>
          <p className="mt-3 text-muted-foreground leading-relaxed">A world where getting paid is as easy as writing a message. No paywalls. No friction.</p>
        </div>
      </div>

      <div className="mb-20">
        <h2 className="font-heading text-3xl font-bold tracking-tight mb-8">Why we exist</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Zap, t: "Fast", d: "Draft and download a polished invoice in under 2 minutes." },
            { icon: Users, t: "Human", d: "Built by people who freelance. Designed for real workflows." },
            { icon: Award, t: "Premium", d: "SaaS-grade design and print output. Free forever." },
          ].map((v) => (
            <div key={v.t} className="rounded-2xl bg-card border border-border p-6">
              <v.icon size={22} className="text-blue-600 mb-3" />
              <div className="font-heading font-semibold">{v.t}</div>
              <p className="text-sm text-muted-foreground mt-1">{v.d}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-3xl bg-blue-50/50 dark:bg-blue-950/20 border border-border p-10">
        <h2 className="font-heading text-2xl font-bold tracking-tight mb-8">Our Journey</h2>
        <div className="space-y-6">
          {[
            ["2024", "The idea", "Frustrated by clunky invoice tools, we sketched the first version on a napkin."],
            ["2025", "Public launch", "Simple Invoice went live — 10K users in the first three months."],
            ["2026", "Growing", "100K+ invoices created. Still 100% free, still no signup."],
          ].map(([y, t, d]) => (
            <div key={y} className="grid grid-cols-[80px_1fr] gap-6">
              <div className="font-heading font-bold text-blue-600 text-lg">{y}</div>
              <div>
                <div className="font-heading font-semibold">{t}</div>
                <div className="text-sm text-muted-foreground mt-1">{d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12">
        {[["100K+", "Invoices Created"], ["10K+", "Users"], ["100%", "Free Forever"]].map(([k, v]) => (
          <div key={v} className="rounded-2xl bg-card border border-border p-6 text-center">
            <div className="font-heading text-3xl font-bold text-blue-600">{k}</div>
            <div className="text-sm text-muted-foreground mt-1">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
